import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import AddressAutocomplete from '../common/AddressAutocomplete';
import AreasInput from './AreasInput';
import { crmService } from '../../services/crm.service';
import {
  LEAD_TYPES,
  RELATIONSHIPS,
  SOURCES,
  buysProperty,
  crmErrorMessage,
  emptyPersonForm,
  formToRequest,
  personToForm,
  sellsProperty,
  validatePersonForm,
} from '../../utils/crm';

const initial = (person) => (person
  ? { id: person.id, form: personToForm(person), saving: false, error: '', duplicateId: null }
  : { id: null, form: emptyPersonForm(), saving: false, error: '', duplicateId: null });

/**
 * The add / edit person dialog, shared by the Contacts page (add, edit) and a lead's page (edit).
 * `person` is null to add. On a duplicate email or phone it offers to switch to the existing person.
 * The caller closes it: onSaved after a successful save, onClose on Cancel.
 */
const PersonEditorDialog = ({ book, ownerUserId, person, onClose, onSaved }) => {
  const [editor, setEditor] = useState(() => initial(person));

  const closeEditor = onClose;
  const setField = (name, value) => setEditor((e) => ({ ...e, form: { ...e.form, [name]: value }, error: '' }));
  // Picking a Google suggestion fills the street, city, state and ZIP at once. The unit is left
  // as typed, and every field stays editable afterwards.
  const pickPropertyAddress = (parsed) => setEditor((e) => ({
    ...e,
    form: {
      ...e.form,
      propertyAddress: {
        ...e.form.propertyAddress,
        line1: parsed.street || e.form.propertyAddress.line1,
        city: parsed.city || '',
        state: parsed.stateCode || '',
        zip: parsed.zip || '',
      },
    },
    error: '',
  }));
  const setPropertyField = (name, value) => setEditor((e) => ({
    ...e,
    form: { ...e.form, propertyAddress: { ...e.form.propertyAddress, [name]: value } },
    error: '',
  }));

  const save = async (event) => {
    event.preventDefault();
    const { form, id } = editor;
    const problem = validatePersonForm(form);
    if (problem) {
      setEditor((e) => ({ ...e, error: problem }));
      return;
    }
    setEditor((e) => ({ ...e, saving: true, error: '', duplicateId: null }));
    try {
      if (id) {
        await crmService.updatePerson(book, id, formToRequest(form, true), ownerUserId);
      } else {
        await crmService.createPerson(book, formToRequest(form, false), ownerUserId);
      }
      onSaved();
    } catch (err) {
      setEditor((e) => ({
        ...e,
        saving: false,
        error: crmErrorMessage(err),
        duplicateId: err?.response?.status === 409 ? err.response.data?.existingPersonId || null : null,
      }));
    }
  };

  const openExisting = async () => {
    try {
      const existing = await crmService.getPerson(book, editor.duplicateId, ownerUserId);
      if (!existing.decryptionFailed) setEditor(initial(existing));
    } catch (err) {
      setEditor((e) => ({ ...e, error: crmErrorMessage(err), duplicateId: null }));
    }
  };

  return createPortal(

    <div className="crm-modal-backdrop">
      <form className="crm-modal" role="dialog" aria-modal="true" aria-label={editor.id ? 'Edit person' : 'Add person'} onSubmit={save}>
        <h2>{editor.id ? 'Edit person' : 'Add person'}</h2>
        <div className="crm-form-grid">
          <label>First name
            <input className="crm-input" value={editor.form.firstName} onChange={(e) => setField('firstName', e.target.value)} maxLength={100} />
          </label>
          <label>Last name
            <input className="crm-input" value={editor.form.lastName} onChange={(e) => setField('lastName', e.target.value)} maxLength={100} />
          </label>
          <label>Email
            <input className="crm-input" type="email" value={editor.form.email} onChange={(e) => setField('email', e.target.value)} maxLength={254} />
          </label>
          <label>Phone
            <input className="crm-input" type="tel" value={editor.form.phone} onChange={(e) => setField('phone', e.target.value)} maxLength={30} />
          </label>
          <label>Relationship
            <select className="crm-select" value={editor.form.relationship} onChange={(e) => setField('relationship', e.target.value)}>
              {RELATIONSHIPS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
          {!editor.id && (
            <label>Source
              <select className="crm-select" value={editor.form.source} onChange={(e) => setField('source', e.target.value)}>
                {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
          )}
        </div>
        <label>Buyer or seller{editor.form.relationship === 'LEAD' ? ' *' : ''}
          <select className="crm-select" value={editor.form.leadType} onChange={(e) => setField('leadType', e.target.value)}>
            <option value="">{editor.form.relationship === 'LEAD' ? 'Choose…' : 'Not set'}</option>
            {LEAD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>

        {sellsProperty(editor.form.leadType) && (
          <fieldset className="crm-fieldset">
            <legend>Property to sell</legend>
            <div className="crm-field">
              <span className="crm-field-label">Street address</span>
              <AddressAutocomplete
                value={editor.form.propertyAddress.line1}
                onChange={(text) => setPropertyField('line1', text)}
                onAddressSelect={pickPropertyAddress}
                placeholder="Start typing the property address"
                ariaLabel="Street address"
                inputClassName="crm-input"
              />
              <span className="crm-hint">Pick a suggestion to fill in the city, state and ZIP code.</span>
            </div>
            <div className="crm-form-grid">
              <label>Unit
                <input className="crm-input" value={editor.form.propertyAddress.line2} onChange={(e) => setPropertyField('line2', e.target.value)} maxLength={100} />
              </label>
              <label>City
                <input className="crm-input" value={editor.form.propertyAddress.city} onChange={(e) => setPropertyField('city', e.target.value)} maxLength={100} />
              </label>
              <label>State
                <input className="crm-input" value={editor.form.propertyAddress.state} onChange={(e) => setPropertyField('state', e.target.value)} maxLength={100} />
              </label>
              <label>ZIP code
                <input className="crm-input" value={editor.form.propertyAddress.zip} onChange={(e) => setPropertyField('zip', e.target.value)} maxLength={20} />
              </label>
            </div>
          </fieldset>
        )}

        {buysProperty(editor.form.leadType) && (
          <div className="crm-field">
            <span className="crm-field-label">Areas of interest</span>
            <AreasInput value={editor.form.areas} onChange={(areas) => setField('areas', areas)} />
            <span className="crm-hint">Add every city, neighborhood or ZIP code the buyer is looking in. Pick a suggestion, or type one and press Enter.</span>
          </div>
        )}

        <label>Tags (separate with commas)
          <input className="crm-input" value={editor.form.tags} onChange={(e) => setField('tags', e.target.value)} />
        </label>
        <label>Notes
          <textarea className="crm-input crm-textarea" rows={4} value={editor.form.notes} onChange={(e) => setField('notes', e.target.value)} maxLength={5000} />
        </label>

        {editor.error && <p className="crm-error" role="alert">{editor.error}</p>}
        {editor.duplicateId && (
          <button type="button" className="crm-link" onClick={openExisting}>Open the existing person</button>
        )}

        <div className="crm-modal-actions">
          <button type="button" className="crm-btn" onClick={closeEditor} disabled={editor.saving}>Cancel</button>
          <button type="submit" className="crm-btn crm-btn-primary" disabled={editor.saving}>{editor.saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div>,
    document.body
  );
};

export default PersonEditorDialog;
